import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';

/** Keeps native and Material tables resizable, including headers rendered after loading. */
@Directive({
  selector: 'table',
  standalone: false,
  host: { class: 'app-resizable-table' }
})
export class ResizableTableDirective implements AfterViewInit, OnDestroy {
  private readonly table = this.element.nativeElement;
  private readonly minWidth = 64;
  private headers: HTMLTableCellElement[] = [];
  private widths: number[] = [];
  private columns?: HTMLTableColElement;
  private observer?: MutationObserver;
  private resizeObserver?: ResizeObserver;
  private removeListeners: (() => void)[] = [];
  private stopDrag?: () => void;
  private readonly originalWidth = this.table.style.getPropertyValue('width');
  private readonly originalWidthPriority = this.table.style.getPropertyPriority('width');

  constructor(private element: ElementRef<HTMLTableElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.updateAccessibleWidths());
      this.refreshHeaders();
      this.observer = new MutationObserver(() => this.refreshHeaders());
      this.observer.observe(this.table, { childList: true, subtree: true });
    });
  }

  private refreshHeaders(): void {
    // table.rows excludes rows belonging to nested detail tables.
    const row = Array.from(this.table.rows).find(row =>
      row.cells.length > 0 && Array.from(row.cells).every(cell => cell.tagName === 'TH' && cell.colSpan === 1)
    );
    const headers = row ? Array.from(row.cells) : [];
    if (headers.length === this.headers.length && headers.every((header, i) => header === this.headers[i])) return;

    this.clearHeaders();
    this.headers = headers;
    headers.forEach((header, index) => {
      const handle = this.table.ownerDocument.createElement('span');
      handle.className = 'app-column-resize-handle';
      handle.tabIndex = 0;
      handle.setAttribute('role', 'separator');
      handle.setAttribute('aria-orientation', 'vertical');
      handle.setAttribute('aria-label', `Resize ${header.textContent?.trim() || `column ${index + 1}`}`);
      handle.setAttribute('aria-valuemin', String(this.minWidth));
      handle.setAttribute('aria-valuenow', String(Math.round(header.getBoundingClientRect().width)));
      handle.title = 'Drag to resize, or use the left and right arrow keys';
      header.classList.add('app-resizable-header');
      header.appendChild(handle);
      this.resizeObserver?.observe(header);

      const pointerdown = (event: PointerEvent) => this.startDrag(event, index);
      const keydown = (event: KeyboardEvent) => {
        event.stopPropagation();
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.captureWidths();
        const step = event.shiftKey ? 64 : 16;
        this.resize(index, this.widths[index] + (event.key === 'ArrowRight' ? step : -step));
      };
      const stop = (event: Event) => event.stopPropagation();
      handle.addEventListener('pointerdown', pointerdown);
      handle.addEventListener('keydown', keydown);
      handle.addEventListener('click', stop);
      handle.addEventListener('dblclick', stop);
      this.removeListeners.push(() => {
        handle.removeEventListener('pointerdown', pointerdown);
        handle.removeEventListener('keydown', keydown);
        handle.removeEventListener('click', stop);
        handle.removeEventListener('dblclick', stop);
        handle.remove();
        header.classList.remove('app-resizable-header');
      });
    });
    this.updateAccessibleWidths();
  }

  private updateAccessibleWidths(): void {
    this.headers.forEach(header => {
      header.querySelector('.app-column-resize-handle')?.setAttribute('aria-valuenow', String(Math.round(header.getBoundingClientRect().width)));
    });
  }

  private captureWidths(): void {
    if (this.widths.length) return;
    this.widths = this.headers.map(header => Math.max(this.minWidth, header.getBoundingClientRect().width));
    this.columns = this.table.ownerDocument.createElement('colgroup');
    this.widths.forEach(width => {
      const column = this.table.ownerDocument.createElement('col');
      column.style.width = `${width}px`;
      this.columns!.appendChild(column);
    });
    this.table.prepend(this.columns);
    this.table.classList.add('app-table-resized');
  }

  private resize(index: number, width: number): void {
    this.widths[index] = Math.max(this.minWidth, width);
    (this.columns!.children[index] as HTMLElement).style.width = `${this.widths[index]}px`;
    // Override utility classes such as Bootstrap's w-100; neighbours keep their widths.
    this.table.style.setProperty('width', `${this.widths.reduce((sum, value) => sum + value, 0)}px`, 'important');
    this.headers.forEach((header, i) => {
      header.querySelector('.app-column-resize-handle')?.setAttribute('aria-valuenow', String(Math.round(this.widths[i])));
    });
  }

  private startDrag(event: PointerEvent, index: number): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.stopDrag?.();
    this.captureWidths();
    const startWidth = this.widths[index];
    const startX = event.clientX;
    const document = this.table.ownerDocument;
    const move = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId === event.pointerId) this.resize(index, startWidth + moveEvent.clientX - startX);
    };
    const finish = (upEvent: PointerEvent) => {
      if (upEvent.pointerId === event.pointerId) this.stopDrag?.();
    };
    document.body.classList.add('app-column-resizing');
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', finish);
    document.addEventListener('pointercancel', finish);
    this.stopDrag = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      document.body.classList.remove('app-column-resizing');
      this.stopDrag = undefined;
    };
  }

  private clearHeaders(): void {
    this.stopDrag?.();
    this.resizeObserver?.disconnect();
    this.removeListeners.forEach(remove => remove());
    this.removeListeners = [];
    this.columns?.remove();
    this.columns = undefined;
    this.widths = [];
    this.table.classList.remove('app-table-resized');
    this.table.style.setProperty('width', this.originalWidth, this.originalWidthPriority);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.clearHeaders();
  }
}
